import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAbility } from '../contexts/AbilityContext';
import LoadingSpinner from './LoadingSpinner';
import type { PermissionInput } from '../types/auth';
import { routeAccess, type RouteGate } from '../router/route-access';
import type { FeatureKey } from '../types/feature-keys.generated';
import type { CapKey, GlobalTipoCode } from '../lib/authz';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
  /** Rank mínimo (hierárquico) — espelha o gate equivalente do backend. */
  minRank?: number | GlobalTipoCode;
  /** Capability do modelo unificado — espelha o gate equivalente do backend. */
  requiredCaps?: CapKey | CapKey[];
  requiredPermissions?: PermissionInput | PermissionInput[];
  permissionMode?: 'all' | 'any';
  requiredCapabilities?: keyof import('../types/auth').UserCapabilities | Array<keyof import('../types/auth').UserCapabilities>;
  features?: FeatureKey | FeatureKey[];
  /** Rota de leitura: acessível sem `can_edit_own_records`. */
  allowReadOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallbackPath = '/login',
  minRank,
  requiredCaps,
  requiredPermissions,
  permissionMode = 'all',
  requiredCapabilities,
  features,
  allowReadOnly,
}) => {
  const { isAuthenticated, token, isLoading, hasRank, hasCap, capabilities, isFeatureEnabled } = useAuth();
  const location = useLocation();
  const { ability } = useAbility();

  // Mostrar loading durante a verificação inicial de autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <LoadingSpinner 
          size="lg" 
          text="Verificando autenticação..." 
          className="text-white"
        />
      </div>
    );
  }

  // Se não estiver autenticado, redirecionar para login
  if (!isAuthenticated || !token) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location.pathname + location.search }}
        replace 
      />
    );
  }

  const gate: RouteGate = {
    minRank, requiredCaps, requiredPermissions, permissionMode,
    requiredCapabilities, requiredFeatures: features, allowReadOnly,
  };
  const access = routeAccess(gate, {
    hasRank, hasCap, capabilities, isFeatureEnabled,
    can: (a, s) => ability.can(a, s),
  });
  if (access === 'read-only') {
    // Confinamento de leitura (sem `can_edit_own_records`): volta pro painel
    // se o usuário o vê; senão /perfil, que é allowReadOnly → encerra a
    // cadeia (sem loop).
    return <Navigate to={hasCap('can_view_painel') ? '/painel-aduaneiro' : '/perfil'} replace />;
  }
  if (access === 'denied') {
    return <Navigate to="/access-denied" replace />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
